import { Base, importWhenVisible } from '@studiometa/js-toolkit';
import { domScheduler, damp } from '@studiometa/js-toolkit/utils';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger.js';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin.js';
import ScreenIntro from './ScreenIntro.js';
import ScreenInfo from './ScreenInfo.js';
import ScreenDesign from './ScreenDesign.js';
import ScrollingListChamonix from './ScrollingListChamonix.js';
import ScreenBento from './ScreenBento.js';
import ScreenPhone from './ScreenPhone.js';
import PanelInfo from './PanelInfo.js';
import PanelInfoOutro from './PanelInfoOutro.js';
import ScreenPhoneIntro from './ScreenPhoneIntro.js';

gsap.registerPlugin(ScrollTrigger);
gsap.registerPlugin(ScrollToPlugin);

/**
 * StellarX5s component
 */
export default class StellarX5s extends Base {
  /**
   * Component Configuration
   */
  static config = {
    name: 'StellarX5s',
    refs: ['video', 'overlay', 'vidintro', 'overlay-wrapper', 'loader', 'altitude', 'step[]'],
    components: {
      ScreenIntro,
      ScreenDesign,
      ScreenInfo,
      ScreenPhone,
      ScreenBento,
      ScreenPhoneIntro,
      ScrollingListChamonix,
      PanelInfo,
      PanelInfoOutro,
      ScreenTitle: (app) => importWhenVisible(() => import('./ScreenTitle.js'), 'ScreenTitle', app),
    },
    options: {
      videoLength: {
        type: Number,
        default: 93,
      },
      maxVelocity: {
        type: Number,
        default: 0.0005,
      },
      videoUpdateInterval: {
        type: Number,
        default: 33,
      },
      altitudeUpdateInterval: {
        type: Number,
        default: 100,
      },
    },
  };

  /**
   * Get vidintro element
   * @returns {HTMLElement}
   */
  get vidintro() {
    return this.$refs.vidintro;
  }

  breakpoints = ['xxs', 'xs', 's'];

  /**
   * Check if device is mobile
   * @returns {boolean}
   */
  get isMobile() {
    return this.breakpoints.includes(this.$services.get('resized').breakpoint);
  }

  /**
   * Mounted hook
   */
  mounted() {
    this.mainVideoReady = false;
    this.introVideoReady = false;
    this.allVideosLoaded = false;

    // Pause videos initially
    this.$refs.vidintro.pause();
    this.$refs.video.pause();

    this.virtualProgress = 0;
    this.lastRealProgress = 0;
    this.lastFrameTime = performance.now();
    this.lastVideoUpdateTime = 0;
    this.lastAltitudeUpdateTime = 0;

    this.cachedVideoLength = this.$options.videoLength;
    this.cachedMaxVelocity = this.$options.maxVelocity;
    this.cachedVideoUpdateInterval = this.$options.videoUpdateInterval;
    this.cachedAltitudeUpdateInterval = this.$options.altitudeUpdateInterval;
    this.maxAltitude = 4806;
    this.minAltitude = 558;

    this.currentActiveStep = null;

    if (this.$options.debug) {
      this.createDebugDisplay();
    }

    this.loadVideos();
    this.setupStepNavigation();
  }

  /**
   * Load videos
   */
  loadVideos() {
    this.loadIntroVideo();
    this.loadMainVideo();
  }

  /**
   * Load main video
   */
  loadMainVideo() {
    this.$refs.video.addEventListener(
      'loadeddata',
      () => {
        this.mainVideoReady = true;
        this.checkAllVideosLoaded();
      },
      { once: true },
    );

    if (this.isMobile) {
      this.$refs.video.src = this.$refs.video.dataset.srcMobile;
    } else {
      this.$refs.video.src = this.$refs.video.dataset.src;
    }
    this.$refs.video.load();
  }

  /**
   * Load intro video
   */
  loadIntroVideo() {
    if (this.isMobile) {
      this.$refs.vidintro.classList.add('hidden');
      this.introVideoReady = true;
    } else {
      this.$refs.vidintro.addEventListener(
        'loadeddata',
        () => {
          this.introVideoReady = true;
          this.checkAllVideosLoaded();
        },
        { once: true },
      );
      this.$refs.vidintro.src = this.$refs.vidintro.dataset.src;
      this.$refs.vidintro.load();
    }
  }

  /**
   * Check if all videos are loaded
   */
  checkAllVideosLoaded() {
    if (this.mainVideoReady && this.introVideoReady && !this.allVideosLoaded) {
      this.allVideosLoaded = true;
      this.onVideosReady();
    }
  }

  /**
   * Set video time based on current scroll position
   */
  async setVideoTimeFromScrollPosition() {
    const scrollY = window.scrollY;
    const documentHeight = document.documentElement.scrollHeight - window.innerHeight;
    const scrollProgress = Math.min(scrollY / documentHeight, 1);
    const targetTime = scrollProgress * this.cachedVideoLength;

    if (this.$refs.video && targetTime > 0) {
      this.$refs.video.currentTime = targetTime;
    }
  }

  /**
   * Callback when all videos are ready
   */
  async onVideosReady() {
    const tl = gsap.timeline();

    tl.to(this.$refs.loader, {
      opacity: 0,
      delay: 1,
      duration: 1.5,
      ease: 'expo.out',
      onStart: async () => {
        this.setVideoTimeFromScrollPosition();
        const event = new CustomEvent('videosready');
        window.dispatchEvent(event);
        await this.$refs.vidintro.play();
      },
    });

    tl.to(
      'body',
      {
        duration: 0.1,
        onComplete: () => {
          this.$refs.loader.style.display = 'none';
          document.body.classList.remove('overflow-hidden');
        },
      },
      '-=1',
    );
  }

  /**
   * Create a debug display
   */
  createDebugDisplay() {
    this.timeDisplay = document.createElement('div');
    this.timeDisplay.style.cssText = `
      position: fixed;
      bottom: 20px;
      right: 20px;
      background: rgba(0, 0, 0, 0.8);
      color: white;
      padding: 8px 12px;
      border-radius: 4px;
      font-family: monospace;
      font-size: 14px;
      z-index: 9999;
      pointer-events: none;
    `;

    this.$el.append(this.timeDisplay);

    this.updateTimeDisplay();
  }

  /**
   * Update debug display with current video time, px scrolled, % of page scroll
   * @param {number} y
   * @param {number} realProgress
   * @param {number} virtualProgress
   */
  updateTimeDisplay(y = 0, realProgress = 0, virtualProgress = 0) {
    if (this.timeDisplay && this.$refs.video) {
      const currentTime = this.$refs.video.currentTime || 0;

      const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
      };

      const yVh = ((y / window.innerHeight) * 100).toFixed(1);
      const displayText = `Real: ${(realProgress * 100).toFixed(2)}% | Virtual: ${(virtualProgress * 100).toFixed(2)}% | ${y}px ${yVh}vh | ${formatTime(currentTime)} / ${formatTime(this.cachedVideoLength)}`;

      domScheduler.write(() => {
        this.timeDisplay.textContent = displayText;
      });
    }
  }

  /**
   * Service scrolled hook
   * @see https://js-toolkit.studiometa.dev/api/methods-hooks-services.html#scrolled
   * @param {object} props
   * @param {object} props.y The current vertical scroll position.
   * @param {object} props.progress The scroll position on both axis mapped to a 0 to 1 range, based on the max property.
   * @param {object} props.max The max scroll position.
   */
  scrolled({ y, progress, max }) {
    const currentTime = performance.now();
    const deltaTime = currentTime - this.lastFrameTime;
    const realProgress = progress.y;

    const progressDelta = realProgress - this.lastRealProgress;
    const velocity = deltaTime > 0 ? Math.abs(progressDelta) / deltaTime : 0;

    let adjustedProgress = realProgress;

    if (velocity > this.cachedMaxVelocity && deltaTime > 0) {
      const direction = progressDelta >= 0 ? 1 : -1;
      const maxChange = this.cachedMaxVelocity * deltaTime;
      const actualChange = Math.min(Math.abs(progressDelta), maxChange);
      adjustedProgress = this.virtualProgress + direction * actualChange;

      adjustedProgress = Math.max(0, Math.min(1, adjustedProgress));
    }

    this.virtualProgress = adjustedProgress;
    const targetTime = this.virtualProgress * this.cachedVideoLength;

    if (this.allVideosLoaded && this.$refs.video) {
      const timeSinceLastVideoUpdate = currentTime - this.lastVideoUpdateTime;

      if (timeSinceLastVideoUpdate >= this.cachedVideoUpdateInterval) {
        try {
          const currentVideoTime = this.$refs.video.currentTime;
          const dampedTime = damp(currentVideoTime, targetTime, 0.05);

          domScheduler.write(() => {
            this.$refs.video.currentTime = dampedTime;
          });

          this.lastVideoUpdateTime = currentTime;
        } catch (error) {
          console.error('Error setting video currentTime:', error);
        }
      }

      if (this.$options.debug) {
        this.updateTimeDisplay(y, realProgress, this.virtualProgress);
      }
    }

    const timeSinceLastAltitudeUpdate = currentTime - this.lastAltitudeUpdateTime;
    if (timeSinceLastAltitudeUpdate >= this.cachedAltitudeUpdateInterval) {
      this.updateAltitude();
      this.lastAltitudeUpdateTime = currentTime;
    }

    this.lastRealProgress = realProgress;
    this.lastFrameTime = currentTime;

    if (y >= max.y - 1) {
      const outroMoreInfos = document.querySelector('#outro-more-infos');
      const panelInfoOutro = document.querySelector('#panel-info-outro');
      if (!panelInfoOutro.classList.contains('is-open')) {
        outroMoreInfos.click();
      }
    }
  }

  /**
   * Update altitude
   */
  updateAltitude() {
    const altitude =
      this.minAltitude + (this.maxAltitude - this.minAltitude) * (1 - this.virtualProgress);
    const altitudeValue = `${Math.round(altitude)}M`;
    domScheduler.write(() => {
      this.$refs.altitude.textContent = altitudeValue;
    });
  }

  /**
   * Setup event-driven step navigation
   */
  setupStepNavigation() {
    const steps = this.$refs.step.sort(this.sorter);
    if (steps[0]) {
      steps[0].classList.add('is-active');
      this.currentActiveStep = steps[0];
    }
  }

  /**
   * On step click
   * @param {object} props
   * @param {Event} props.event
   */
  onStepClick({ event }) {
    event.preventDefault();
    const target = event.target;
    const href = target.getAttribute('href');
    if (href) {
      gsap.to(window, {
        scrollTo: {
          y: href,
          offsetY: 100,
        },
        duration: 1,
        ease: 'power1.out',
      });
    }
  }

  /**
   * Handle ScreenTitle active event
   * @param {object} data The emitted data from ScreenTitle
   * @param {string} data.name The name of the active ScreenTitle
   */
  onScreenTitleActive(data) {
    this.updateActiveStep(data.args[0]);
  }

  /**
   * Update active step when ScreenTitle becomes active
   * @param {string} screenTitleName
   */
  updateActiveStep(screenTitleName) {
    const steps = this.$refs.step.sort(this.sorter);
    const targetHref = `#${screenTitleName}`;
    const newActiveStep = steps.find((step) => step.getAttribute('href') === targetHref);

    // Only update if the active step actually changed
    if (newActiveStep && newActiveStep !== this.currentActiveStep) {
      steps.forEach((element) => {
        element.classList.remove('is-active');
      });

      // Add is-active to new step
      newActiveStep.classList.add('is-active');
      this.currentActiveStep = newActiveStep;
    }
  }
}
